import React, {useMemo, useState} from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  adminActivitiesCache,
  adminChallengesCache,
  adminChallengeTemplatesCache,
  adminTagsCache,
} from '../../../common/constants/queryCacheKey';
import {
  AdminChallenge,
  ChallengeGoalType,
  ChallengeStatus,
  ChallengeTemplate,
  createChallenge,
  createChallengeTemplate,
  endChallenge,
  fetchAdminChallenges,
  fetchChallengeTemplates,
  LocalizedText,
  publishChallenge,
  SaveChallengeInput,
  SaveChallengeTemplateInput,
  updateChallenge,
  updateChallengeTemplate,
} from '../../../common/services/challenges';
import {fetchAdminTags} from '../../../common/services/tags';
import {
  AdminActivity,
  fetchAdminActivities,
} from '../../../common/services/activities';
import {useQueryLocationTags} from '../../../common/hooks/useLocationTags';
import {DashDropdown} from '../../dropdowns/DashDropdown';
import {FormDatePicker} from '../../forms/FormDatePicker';
import {SearchInput} from '../../search/SearchInput';
import {useGuardedMutate} from '../../../common/hooks/useGuardedMutate';
import {
  badgeStyle,
  chipStyle,
  dangerButtonStyle,
  disabledButtonStyle,
  errorStyle,
  inputStyle,
  labelStyle,
  messageStyle,
  noticeStyle,
  panelStyle,
  primaryButtonStyle,
  screenStyle,
  secondaryButtonStyle,
  titleStyle,
} from '../../admin/adminStyles';

// ---------- Metadata ----------

interface GoalMeta {
  label: string;
  hint: string;
  needsTags?: boolean;
  needsActivities?: boolean;
}

const GOAL_META: Record<ChallengeGoalType, GoalMeta> = {
  VisitNewPlaces: {
    label: 'Visit new places',
    hint: 'Verified visits at places the user has never been to.',
  },
  VisitCategory: {
    label: 'Visit a category',
    hint: 'Verified visits at places or events with one of the chosen tags.',
    needsTags: true,
  },
  VisitAnyOf: {
    label: 'Visit one of',
    hint: 'Verified visits at the chosen places or events.',
    needsActivities: true,
  },
  PostMoods: {label: 'Post moods', hint: 'Moods shared.'},
  WriteReviews: {label: 'Write reviews', hint: 'Reviews written.'},
  VisitWithFriend: {
    label: 'Visit with a friend',
    hint: 'Verified visits on the same day as a friend.',
  },
  HonorBookings: {label: 'Honor bookings', hint: 'Bookings shown up for.'},
};

const GOAL_TYPES = Object.keys(GOAL_META) as ChallengeGoalType[];
// The weekly draw cannot pick specific activities.
const TEMPLATE_GOAL_TYPES = GOAL_TYPES.filter(type => type !== 'VisitAnyOf');

const goalLabel = (type: string) =>
  (GOAL_META as Record<string, GoalMeta | undefined>)[type]?.label ?? type;

const STATUS_FILTERS: {label: string; value?: ChallengeStatus}[] = [
  {label: 'All'},
  {label: 'Draft', value: 'Draft'},
  {label: 'Published', value: 'Published'},
  {label: 'Ended', value: 'Ended'},
];

const statusColors: Record<ChallengeStatus, string> = {
  Draft: '#555',
  Published: '#2f6b2f',
  Ended: '#7a2626',
};

const LANGUAGES: {key: keyof LocalizedText; label: string}[] = [
  {key: 'en', label: 'EN'},
  {key: 'ro', label: 'RO'},
  {key: 'pt', label: 'PT'},
];

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 500;
const PAGE_SIZE = 20;
const EVERYWHERE = 0;

// ---------- Helpers ----------

const formatDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

// A challenge whose end has passed can no longer be published.
const isPastEnd = (challenge: AdminChallenge): boolean =>
  new Date(challenge.endsAt).getTime() <= Date.now();

const emptyText = (): LocalizedText => ({en: '', ro: '', pt: ''});

const trimText = (text: LocalizedText): LocalizedText => ({
  en: text.en.trim(),
  ro: text.ro.trim(),
  pt: text.pt.trim(),
});

const textError = (
  text: LocalizedText,
  name: string,
  max: number,
): string | null => {
  for (const {key, label} of LANGUAGES) {
    const value = text[key].trim();
    if (!value) return `The ${name} is required in ${label}.`;
    if (value.length > max)
      return `The ${name} in ${label} is longer than ${max} characters.`;
  }
  return null;
};

const integerIn = (text: string, min: number, max: number): number | null => {
  const value = Number(text.trim());
  return text.trim() !== '' &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
    ? value
    : null;
};

// ---------- Shared editor pieces ----------

const LocalizedFields: React.FC<{
  label: string;
  value: LocalizedText;
  max: number;
  multiline?: boolean;
  onChange: (value: LocalizedText) => void;
}> = ({label, value, max, multiline, onChange}) => (
  <div style={localizedGridStyle}>
    {LANGUAGES.map(({key, label: language}) => (
      <label key={key} style={labelStyle}>
        {label} ({language})
        {multiline ? (
          <textarea
            maxLength={max}
            value={value[key]}
            onChange={e => onChange({...value, [key]: e.target.value})}
            style={textareaStyle}
          />
        ) : (
          <input
            maxLength={max}
            value={value[key]}
            onChange={e => onChange({...value, [key]: e.target.value})}
            style={inputStyle}
          />
        )}
      </label>
    ))}
  </div>
);

const TagPicker: React.FC<{
  selected: number[];
  disabled?: boolean;
  onChange: (codes: number[]) => void;
}> = ({selected, disabled, onChange}) => {
  const [filter, setFilter] = useState('');
  const tagsQuery = useQuery({
    queryKey: [adminTagsCache],
    queryFn: fetchAdminTags,
  });

  const toggle = (code: number) => {
    if (disabled) return;
    onChange(
      selected.includes(code)
        ? selected.filter(c => c !== code)
        : [...selected, code],
    );
  };

  const data = tagsQuery.data;
  const keyword = filter.trim().toLowerCase();
  const groups = (data?.categories ?? [])
    .map(category => ({
      category,
      tags: (data?.tags ?? []).filter(
        tag =>
          tag.categoryCode === category.code &&
          (!keyword || tag.name.toLowerCase().includes(keyword)),
      ),
    }))
    .filter(group => group.tags.length > 0);

  return (
    <div style={{...pickerStyle, opacity: disabled ? 0.5 : 1}}>
      <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
        <input
          placeholder="Filter tags..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{...inputStyle, flex: 1}}
        />
        <span style={{color: '#999', fontSize: '0.8rem', flexShrink: 0}}>
          {selected.length} selected
        </span>
      </div>
      {tagsQuery.isPending && <p style={loadingStyle}>Loading...</p>}
      {tagsQuery.isError && <p style={errorStyle}>Could not load tags.</p>}
      {groups.map(({category, tags}) => (
        <div key={category.code}>
          <div style={groupTitleStyle}>{category.name}</div>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
            {tags.map(tag => (
              <button
                key={tag.code}
                type="button"
                disabled={disabled}
                onClick={() => toggle(tag.code)}
                style={chipStyle(selected.includes(tag.code))}>
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

interface PickedActivity {
  id: string;
  name: string;
}

const ActivityPicker: React.FC<{
  selected: PickedActivity[];
  disabled?: boolean;
  onChange: (activities: PickedActivity[]) => void;
}> = ({selected, disabled, onChange}) => {
  const [keyword, setKeyword] = useState('');

  const searchQuery = useQuery({
    queryKey: [adminActivitiesCache, 'challenge-picker', keyword],
    queryFn: () =>
      fetchAdminActivities({
        keyword,
        sortBy: 'name',
        sortDirection: 'asc',
        pageNumber: 1,
        pageSize: 10,
      }),
    enabled: !disabled && keyword.length > 0,
    placeholderData: keepPreviousData,
  });

  const add = (activity: AdminActivity) => {
    if (selected.some(item => item.id === activity.id)) return;
    onChange([...selected, {id: activity.id, name: activity.name}]);
  };

  const results = searchQuery.data?.items ?? [];

  return (
    <div style={{...pickerStyle, opacity: disabled ? 0.5 : 1}}>
      {selected.length === 0 ? (
        <p style={hintStyle}>No places or events chosen yet.</p>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
          {selected.map(item => (
            <div key={item.id} style={pickerRowStyle}>
              <span style={{flex: 1, color: '#fff'}} title={item.id}>
                {item.name}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  onChange(selected.filter(other => other.id !== item.id))
                }
                style={disabled ? disabledButtonStyle : dangerButtonStyle}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <>
          <SearchInput
            placeholder="Search places and events to add..."
            onSearch={setKeyword}
          />
          {keyword && searchQuery.isPending && (
            <p style={loadingStyle}>Searching...</p>
          )}
          {searchQuery.isError && (
            <p style={errorStyle}>Could not search activities.</p>
          )}
          {keyword && !searchQuery.isPending && results.length === 0 && (
            <p style={hintStyle}>Nothing matches.</p>
          )}
          {results.map(activity => {
            const picked = selected.some(item => item.id === activity.id);
            return (
              <div key={activity.id} style={pickerRowStyle}>
                <span style={{flex: 1, minWidth: 0}}>
                  <span style={{color: '#fff'}}>{activity.name}</span>
                  <span style={{color: '#999', fontSize: '0.8rem'}}>
                    {' '}
                    · {activity.type}
                    {activity.city ? ` · ${activity.city}` : ''}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={picked}
                  onClick={() => add(activity)}
                  style={picked ? disabledButtonStyle : secondaryButtonStyle}>
                  {picked ? 'Added' : 'Add'}
                </button>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

// ---------- Challenge editor ----------

interface ChallengeForm {
  title: LocalizedText;
  description: LocalizedText;
  goalType: ChallengeGoalType;
  target: string;
  rewardPoints: string;
  locationCode: number | null;
  startsAt: Date | null;
  endsAt: Date | null;
  maxParticipants: string;
  goalTagCodes: number[];
  goalActivities: PickedActivity[];
}

const toChallengeForm = (challenge?: AdminChallenge): ChallengeForm =>
  challenge
    ? {
        title: {...challenge.title},
        description: {...challenge.description},
        goalType: challenge.goalType,
        target: String(challenge.target),
        rewardPoints: String(challenge.rewardPoints),
        locationCode: challenge.locationCode,
        startsAt: new Date(challenge.startsAt),
        endsAt: new Date(challenge.endsAt),
        maxParticipants:
          challenge.maxParticipants === null
            ? ''
            : String(challenge.maxParticipants),
        goalTagCodes: [...challenge.goalTagCodes],
        // The API sends only the ids, so the names are unknown here.
        goalActivities: challenge.goalActivityIds.map(id => ({
          id,
          name: `Place or event ${id.slice(0, 8)}…`,
        })),
      }
    : {
        title: emptyText(),
        description: emptyText(),
        goalType: 'VisitNewPlaces',
        target: '3',
        rewardPoints: '50',
        locationCode: null,
        startsAt: null,
        endsAt: null,
        maxParticipants: '',
        goalTagCodes: [],
        goalActivities: [],
      };

const validateChallenge = (
  form: ChallengeForm,
  published: boolean,
): SaveChallengeInput | string => {
  const title = trimText(form.title);
  const description = trimText(form.description);
  const error =
    textError(title, 'title', TITLE_MAX) ??
    textError(description, 'description', DESCRIPTION_MAX);
  if (error) return error;

  const target = integerIn(form.target, 1, 100);
  if (target === null) return 'The target must be between 1 and 100.';
  const rewardPoints = integerIn(form.rewardPoints, 0, 1000);
  if (rewardPoints === null) return 'The reward must be between 0 and 1000.';

  let maxParticipants: number | null = null;
  if (form.maxParticipants.trim() !== '') {
    maxParticipants = integerIn(
      form.maxParticipants,
      1,
      Number.MAX_SAFE_INTEGER,
    );
    if (maxParticipants === null)
      return 'Max participants must be at least 1, or empty for no limit.';
  }

  if (!form.startsAt || !form.endsAt) return 'Pick a start and an end.';
  if (form.endsAt <= form.startsAt) return 'The end must be after the start.';
  if (published && form.endsAt.getTime() <= Date.now())
    return 'A published challenge must end in the future.';

  const meta = GOAL_META[form.goalType];
  if (meta.needsTags && form.goalTagCodes.length === 0)
    return 'Pick at least one tag for this goal.';
  if (meta.needsActivities && form.goalActivities.length === 0)
    return 'Pick at least one place or event for this goal.';

  return {
    title,
    description,
    goalType: form.goalType,
    target,
    rewardPoints,
    goalTagCodes: meta.needsTags ? form.goalTagCodes : [],
    goalActivityIds: meta.needsActivities
      ? form.goalActivities.map(item => item.id)
      : [],
    startsAt: form.startsAt.toISOString(),
    endsAt: form.endsAt.toISOString(),
    locationCode: form.locationCode,
    maxParticipants,
  };
};

const ChallengeEditor: React.FC<{
  challenge?: AdminChallenge;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({challenge, onClose, onSaved}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ChallengeForm>(() =>
    toChallengeForm(challenge),
  );
  const [error, setError] = useState<string | null>(null);
  const {data: locations} = useQueryLocationTags();

  // A published challenge only takes the texts, the end and the participant limit.
  const locked = challenge?.status === 'Published';
  const meta = GOAL_META[form.goalType];

  const cityOptions = useMemo(
    () => [{code: EVERYWHERE, name: 'Everywhere'}, ...(locations ?? [])],
    [locations],
  );

  const update = <K extends keyof ChallengeForm>(
    key: K,
    value: ChallengeForm[K],
  ) => setForm(current => ({...current, [key]: value}));

  const saveMutation = useMutation({
    mutationFn: (input: SaveChallengeInput) =>
      challenge ? updateChallenge(challenge.id, input) : createChallenge(input),
    onSuccess: saved => {
      queryClient.invalidateQueries({queryKey: [adminChallengesCache]});
      onSaved(
        challenge
          ? `"${saved.title.en}" was saved.`
          : `"${saved.title.en}" was created as a draft.`,
      );
    },
    onError: (e: Error) => setError(e.message),
  });

  const save = useGuardedMutate(saveMutation.mutate);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = validateChallenge(form, locked);
    if (typeof input === 'string') {
      setError(input);
      return;
    }
    setError(null);
    save(input);
  };

  return (
    <form onSubmit={handleSubmit} style={{...panelStyle, marginBottom: '24px'}}>
      <h2 style={editorTitleStyle}>
        {challenge ? `Edit "${challenge.title.en}"` : 'New challenge'}
      </h2>
      {locked && (
        <p style={{...hintStyle, marginBottom: '12px'}}>
          This challenge is published: only the texts, the end and the max
          participants can change. The other fields are kept as they are.
        </p>
      )}

      <LocalizedFields
        label="Title"
        value={form.title}
        max={TITLE_MAX}
        onChange={value => update('title', value)}
      />
      <LocalizedFields
        label="Description"
        value={form.description}
        max={DESCRIPTION_MAX}
        multiline
        onChange={value => update('description', value)}
      />

      <div style={fieldGridStyle}>
        <label style={labelStyle}>
          Goal
          <select
            value={form.goalType}
            disabled={locked}
            onChange={e =>
              update('goalType', e.target.value as ChallengeGoalType)
            }
            style={locked ? disabledInputStyle : inputStyle}>
            {GOAL_TYPES.map(type => (
              <option key={type} value={type}>
                {GOAL_META[type].label}
              </option>
            ))}
          </select>
          <span style={hintStyle}>{meta.hint}</span>
        </label>
        <label style={labelStyle}>
          Target (1-100)
          <input
            type="number"
            min={1}
            max={100}
            step={1}
            disabled={locked}
            value={form.target}
            onChange={e => update('target', e.target.value)}
            style={locked ? disabledInputStyle : inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Reward points (0-1000)
          <input
            type="number"
            min={0}
            max={1000}
            step={1}
            disabled={locked}
            value={form.rewardPoints}
            onChange={e => update('rewardPoints', e.target.value)}
            style={locked ? disabledInputStyle : inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Max participants (empty = no limit)
          <input
            type="number"
            min={1}
            step={1}
            value={form.maxParticipants}
            onChange={e => update('maxParticipants', e.target.value)}
            style={inputStyle}
          />
        </label>
        <div style={labelStyle}>
          City
          <div
            style={{
              pointerEvents: locked ? 'none' : undefined,
              opacity: locked ? 0.5 : 1,
            }}>
            <DashDropdown
              options={cityOptions}
              selectedValue={form.locationCode ?? EVERYWHERE}
              placeholder="Everywhere"
              onChange={({code}) =>
                update('locationCode', code === EVERYWHERE ? null : code)
              }
            />
          </div>
        </div>
      </div>

      <div style={{...fieldGridStyle, marginTop: '16px'}}>
        <FormDatePicker
          label="Starts"
          selectedDate={form.startsAt}
          onChange={date => update('startsAt', date)}
          disabled={locked}
          required
        />
        <FormDatePicker
          label="Ends"
          selectedDate={form.endsAt}
          onChange={date => update('endsAt', date)}
          required
        />
      </div>

      {meta.needsTags && (
        <div style={{marginTop: '4px'}}>
          <div style={labelStyle}>Tags (one of them must match)</div>
          <TagPicker
            selected={form.goalTagCodes}
            disabled={locked}
            onChange={codes => update('goalTagCodes', codes)}
          />
        </div>
      )}

      {meta.needsActivities && (
        <div style={{marginTop: '4px'}}>
          <div style={labelStyle}>Places and events</div>
          <ActivityPicker
            selected={form.goalActivities}
            disabled={locked}
            onChange={items => update('goalActivities', items)}
          />
        </div>
      )}

      {error && <p style={{...errorStyle, marginTop: '16px'}}>{error}</p>}

      <div style={{display: 'flex', gap: '12px', marginTop: '20px'}}>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          style={primaryButtonStyle}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// ---------- Challenges panel ----------

const ChallengesPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ChallengeStatus | undefined>();
  const [pageNumber, setPageNumber] = useState(1);
  const [editing, setEditing] = useState<AdminChallenge | 'new' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const {data: locations} = useQueryLocationTags();

  const challengesQuery = useQuery({
    queryKey: [adminChallengesCache, status ?? 'all', pageNumber],
    queryFn: () =>
      fetchAdminChallenges({status, pageNumber, pageSize: PAGE_SIZE}),
    placeholderData: keepPreviousData,
  });

  const cityName = (code: number | null) =>
    code === null
      ? 'Everywhere'
      : (locations?.find(location => location.code === code)?.name ??
        `#${code}`);

  const refresh = () =>
    queryClient.invalidateQueries({queryKey: [adminChallengesCache]});

  const publishMutation = useMutation({
    mutationFn: publishChallenge,
    onSuccess: saved => {
      setActionError(null);
      setNotice(`"${saved.title.en}" is now published.`);
      refresh();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const endMutation = useMutation({
    mutationFn: endChallenge,
    onSuccess: saved => {
      setActionError(null);
      setNotice(`"${saved.title.en}" has ended.`);
      refresh();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const handlePublish = (challenge: AdminChallenge) => {
    if (isPastEnd(challenge)) {
      setNotice(null);
      setActionError('The end date is in the past; edit it before publishing.');
      return;
    }
    if (
      window.confirm(
        `Publish "${challenge.title.en}"?\n\nUsers can join it from ${formatDateTime(challenge.startsAt)} until ${formatDateTime(challenge.endsAt)}. The goal, target, reward, city and start can no longer change.`,
      )
    ) {
      setNotice(null);
      publishMutation.mutate(challenge.id);
    }
  };

  const handleEnd = (challenge: AdminChallenge) => {
    if (
      window.confirm(
        `End "${challenge.title.en}" now?\n\n${challenge.participants} participants joined; runs still in progress expire. This cannot be undone.`,
      )
    ) {
      setNotice(null);
      endMutation.mutate(challenge.id);
    }
  };

  const page = challengesQuery.data;
  const items = page?.items ?? [];
  const maxPage = page?.maxPage ?? 0;
  const isActing = publishMutation.isPending || endMutation.isPending;

  return (
    <section style={sectionStyle}>
      <div style={toolbarStyle}>
        <h2 style={{...sectionTitleStyle, margin: 0, flex: 1}}>Challenges</h2>
        <div style={{display: 'flex', gap: '6px'}}>
          {STATUS_FILTERS.map(filter => (
            <button
              key={filter.label}
              type="button"
              onClick={() => {
                setStatus(filter.value);
                setPageNumber(1);
              }}
              style={chipStyle(status === filter.value)}>
              {filter.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setNotice(null);
            setEditing(current => (current === 'new' ? null : 'new'));
          }}
          style={editing === 'new' ? secondaryButtonStyle : primaryButtonStyle}>
          {editing === 'new' ? 'Cancel' : 'New challenge'}
        </button>
      </div>

      {editing && (
        <ChallengeEditor
          key={editing === 'new' ? 'new' : editing.id}
          challenge={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={message => {
            setNotice(message);
            setEditing(null);
          }}
        />
      )}

      {actionError && <p style={errorStyle}>{actionError}</p>}
      {notice && <p style={noticeStyle}>{notice}</p>}

      {challengesQuery.isPending && <p style={loadingStyle}>Loading...</p>}
      {challengesQuery.isError && (
        <p style={errorStyle}>{(challengesQuery.error as Error).message}</p>
      )}
      {page && items.length === 0 && (
        <p style={messageStyle}>No challenges here yet.</p>
      )}

      {items.length > 0 && (
        <div style={{overflowX: 'auto'}}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headStyle}>Title</th>
                <th style={headStyle}>Goal</th>
                <th style={{...headStyle, textAlign: 'right'}}>Target</th>
                <th style={{...headStyle, textAlign: 'right'}}>Reward</th>
                <th style={headStyle}>City</th>
                <th style={headStyle}>Window</th>
                <th style={headStyle}>Status</th>
                <th style={{...headStyle, textAlign: 'right'}}>
                  Joined / done
                </th>
                <th style={headStyle} />
              </tr>
            </thead>
            <tbody>
              {items.map(challenge => (
                <tr key={challenge.id} style={rowStyle}>
                  <td style={{...cellStyle, color: '#fff'}}>
                    {challenge.title.en}
                  </td>
                  <td style={cellStyle}>{goalLabel(challenge.goalType)}</td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    {challenge.target}
                  </td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    {challenge.rewardPoints}
                  </td>
                  <td style={cellStyle}>{cityName(challenge.locationCode)}</td>
                  <td style={{...cellStyle, whiteSpace: 'nowrap'}}>
                    {formatDateTime(challenge.startsAt)}
                    <br />
                    {formatDateTime(challenge.endsAt)}
                  </td>
                  <td style={cellStyle}>
                    <span
                      style={{
                        ...badgeStyle,
                        backgroundColor: statusColors[challenge.status],
                      }}>
                      {challenge.status}
                    </span>
                  </td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    {challenge.participants} / {challenge.completed}
                    {challenge.maxParticipants !== null && (
                      <span style={{color: '#666'}}>
                        {' '}
                        (max {challenge.maxParticipants})
                      </span>
                    )}
                  </td>
                  <td style={{...cellStyle, whiteSpace: 'nowrap'}}>
                    <div style={{display: 'flex', gap: '6px'}}>
                      <button
                        type="button"
                        disabled={challenge.status === 'Ended'}
                        title={
                          challenge.status === 'Ended'
                            ? 'An ended challenge cannot change.'
                            : ''
                        }
                        onClick={() => {
                          setNotice(null);
                          setEditing(challenge);
                          window.scrollTo({top: 0, behavior: 'smooth'});
                        }}
                        style={
                          challenge.status === 'Ended'
                            ? smallDisabledButtonStyle
                            : smallSecondaryButtonStyle
                        }>
                        Edit
                      </button>
                      {challenge.status === 'Draft' && (
                        <button
                          type="button"
                          disabled={isActing || isPastEnd(challenge)}
                          title={
                            isPastEnd(challenge)
                              ? 'End date is in the past'
                              : ''
                          }
                          onClick={() => handlePublish(challenge)}
                          style={
                            isActing || isPastEnd(challenge)
                              ? smallDisabledButtonStyle
                              : smallPrimaryButtonStyle
                          }>
                          Publish
                        </button>
                      )}
                      {challenge.status === 'Published' && (
                        <button
                          type="button"
                          disabled={isActing}
                          onClick={() => handleEnd(challenge)}
                          style={
                            isActing
                              ? smallDisabledButtonStyle
                              : smallDangerButtonStyle
                          }>
                          End
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {maxPage > 1 && (
        <Pager
          page={pageNumber}
          maxPage={maxPage}
          onChange={setPageNumber}
          isFetching={challengesQuery.isFetching}
        />
      )}
    </section>
  );
};

// ---------- Template editor ----------

interface TemplateForm {
  enabled: boolean;
  goalType: ChallengeGoalType;
  title: LocalizedText;
  description: LocalizedText;
  targetMin: string;
  targetMax: string;
  rewardPoints: string;
  goalTagCodes: number[];
  weight: string;
}

const toTemplateForm = (template?: ChallengeTemplate): TemplateForm =>
  template
    ? {
        enabled: template.enabled,
        goalType: template.goalType,
        title: {...template.title},
        description: {...template.description},
        targetMin: String(template.targetMin),
        targetMax: String(template.targetMax),
        rewardPoints: String(template.rewardPoints),
        goalTagCodes: [...template.goalTagCodes],
        weight: String(template.weight),
      }
    : {
        enabled: true,
        goalType: 'VisitNewPlaces',
        title: emptyText(),
        description: emptyText(),
        targetMin: '2',
        targetMax: '5',
        rewardPoints: '50',
        goalTagCodes: [],
        weight: '10',
      };

const toTemplateInput = (
  template: ChallengeTemplate,
): SaveChallengeTemplateInput => ({
  enabled: template.enabled,
  goalType: template.goalType,
  title: template.title,
  description: template.description,
  targetMin: template.targetMin,
  targetMax: template.targetMax,
  rewardPoints: template.rewardPoints,
  goalTagCodes: template.goalTagCodes,
  weight: template.weight,
});

const validateTemplate = (
  form: TemplateForm,
): SaveChallengeTemplateInput | string => {
  const title = trimText(form.title);
  const description = trimText(form.description);
  const error =
    textError(title, 'title', TITLE_MAX) ??
    textError(description, 'description', DESCRIPTION_MAX);
  if (error) return error;

  const targetMin = integerIn(form.targetMin, 1, 100);
  const targetMax = integerIn(form.targetMax, 1, 100);
  if (targetMin === null || targetMax === null)
    return 'The target range must be between 1 and 100.';
  if (targetMax < targetMin)
    return 'The max target must be at least the min target.';
  const rewardPoints = integerIn(form.rewardPoints, 0, 1000);
  if (rewardPoints === null) return 'The reward must be between 0 and 1000.';
  const weight = integerIn(form.weight, 1, 100);
  if (weight === null) return 'The weight must be between 1 and 100.';

  const meta = GOAL_META[form.goalType];
  if (meta.needsTags && form.goalTagCodes.length === 0)
    return 'Pick at least one tag for this goal.';

  return {
    enabled: form.enabled,
    goalType: form.goalType,
    title,
    description,
    targetMin,
    targetMax,
    rewardPoints,
    goalTagCodes: meta.needsTags ? form.goalTagCodes : [],
    weight,
  };
};

const TemplateEditor: React.FC<{
  template?: ChallengeTemplate;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({template, onClose, onSaved}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TemplateForm>(() =>
    toTemplateForm(template),
  );
  const [error, setError] = useState<string | null>(null);
  const meta = GOAL_META[form.goalType];

  const update = <K extends keyof TemplateForm>(
    key: K,
    value: TemplateForm[K],
  ) => setForm(current => ({...current, [key]: value}));

  const saveMutation = useMutation({
    mutationFn: (input: SaveChallengeTemplateInput) =>
      template
        ? updateChallengeTemplate(template.id, input)
        : createChallengeTemplate(input),
    onSuccess: saved => {
      queryClient.invalidateQueries({queryKey: [adminChallengeTemplatesCache]});
      onSaved(
        template
          ? `Template "${saved.title.en}" was saved.`
          : `Template "${saved.title.en}" was added.`,
      );
    },
    onError: (e: Error) => setError(e.message),
  });

  const save = useGuardedMutate(saveMutation.mutate);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = validateTemplate(form);
    if (typeof input === 'string') {
      setError(input);
      return;
    }
    setError(null);
    save(input);
  };

  return (
    <form onSubmit={handleSubmit} style={{...panelStyle, marginBottom: '24px'}}>
      <h2 style={editorTitleStyle}>
        {template ? `Edit template "${template.title.en}"` : 'New template'}
      </h2>
      <p style={{...hintStyle, marginBottom: '12px'}}>
        Write {'{target}'} in a text to show the drawn target, e.g. "Visit{' '}
        {'{target}'} new places this week".
      </p>

      <LocalizedFields
        label="Title"
        value={form.title}
        max={TITLE_MAX}
        onChange={value => update('title', value)}
      />
      <LocalizedFields
        label="Description"
        value={form.description}
        max={DESCRIPTION_MAX}
        multiline
        onChange={value => update('description', value)}
      />

      <div style={fieldGridStyle}>
        <label style={labelStyle}>
          Goal
          <select
            value={form.goalType}
            onChange={e =>
              update('goalType', e.target.value as ChallengeGoalType)
            }
            style={inputStyle}>
            {TEMPLATE_GOAL_TYPES.map(type => (
              <option key={type} value={type}>
                {GOAL_META[type].label}
              </option>
            ))}
          </select>
          <span style={hintStyle}>{meta.hint}</span>
        </label>
        <label style={labelStyle}>
          Target min (1-100)
          <input
            type="number"
            min={1}
            max={100}
            step={1}
            value={form.targetMin}
            onChange={e => update('targetMin', e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Target max (1-100)
          <input
            type="number"
            min={1}
            max={100}
            step={1}
            value={form.targetMax}
            onChange={e => update('targetMax', e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Reward points (0-1000)
          <input
            type="number"
            min={0}
            max={1000}
            step={1}
            value={form.rewardPoints}
            onChange={e => update('rewardPoints', e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Weight (1-100)
          <input
            type="number"
            min={1}
            max={100}
            step={1}
            value={form.weight}
            onChange={e => update('weight', e.target.value)}
            style={inputStyle}
          />
          <span style={hintStyle}>Heavier templates are drawn more often.</span>
        </label>
        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={e => update('enabled', e.target.checked)}
          />
          Enabled
        </label>
      </div>

      {meta.needsTags && (
        <div style={{marginTop: '16px'}}>
          <div style={labelStyle}>Tags (one of them must match)</div>
          <TagPicker
            selected={form.goalTagCodes}
            onChange={codes => update('goalTagCodes', codes)}
          />
        </div>
      )}

      {error && <p style={{...errorStyle, marginTop: '16px'}}>{error}</p>}

      <div style={{display: 'flex', gap: '12px', marginTop: '20px'}}>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          style={primaryButtonStyle}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// ---------- Templates panel ----------

const TemplatesPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ChallengeTemplate | 'new' | null>(
    null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: [adminChallengeTemplatesCache],
    queryFn: fetchChallengeTemplates,
  });

  const toggleMutation = useMutation({
    mutationFn: ({
      template,
      enabled,
    }: {
      template: ChallengeTemplate;
      enabled: boolean;
    }) =>
      updateChallengeTemplate(template.id, {
        ...toTemplateInput(template),
        enabled,
      }),
    onSuccess: saved => {
      setActionError(null);
      setNotice(
        `Template "${saved.title.en}" is now ${saved.enabled ? 'enabled' : 'disabled'}.`,
      );
      queryClient.invalidateQueries({queryKey: [adminChallengeTemplatesCache]});
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const templates = templatesQuery.data ?? [];

  return (
    <section style={sectionStyle}>
      <div style={toolbarStyle}>
        <div style={{flex: 1}}>
          <h2 style={{...sectionTitleStyle, margin: 0}}>Weekly templates</h2>
          <p style={hintStyle}>
            Every Monday each active user gets 3 of these, picked by weight and
            avoiding last week's.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setNotice(null);
            setEditing(current => (current === 'new' ? null : 'new'));
          }}
          style={editing === 'new' ? secondaryButtonStyle : primaryButtonStyle}>
          {editing === 'new' ? 'Cancel' : 'New template'}
        </button>
      </div>

      {editing && (
        <TemplateEditor
          key={editing === 'new' ? 'new' : editing.id}
          template={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={message => {
            setNotice(message);
            setEditing(null);
          }}
        />
      )}

      {actionError && <p style={errorStyle}>{actionError}</p>}
      {notice && <p style={noticeStyle}>{notice}</p>}

      {templatesQuery.isPending && <p style={loadingStyle}>Loading...</p>}
      {templatesQuery.isError && (
        <p style={errorStyle}>{(templatesQuery.error as Error).message}</p>
      )}
      {templatesQuery.isSuccess && templates.length === 0 && (
        <p style={messageStyle}>No templates yet.</p>
      )}

      {templates.length > 0 && (
        <div style={{overflowX: 'auto'}}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{...headStyle, textAlign: 'center'}}>Enabled</th>
                <th style={headStyle}>Goal</th>
                <th style={headStyle}>Title</th>
                <th style={{...headStyle, textAlign: 'right'}}>Target</th>
                <th style={{...headStyle, textAlign: 'right'}}>Reward</th>
                <th style={{...headStyle, textAlign: 'right'}}>Weight</th>
                <th style={headStyle} />
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template.id} style={rowStyle}>
                  <td style={{...cellStyle, textAlign: 'center'}}>
                    <input
                      type="checkbox"
                      checked={template.enabled}
                      disabled={toggleMutation.isPending}
                      onChange={e =>
                        toggleMutation.mutate({
                          template,
                          enabled: e.target.checked,
                        })
                      }
                    />
                  </td>
                  <td style={cellStyle}>{goalLabel(template.goalType)}</td>
                  <td style={{...cellStyle, color: '#fff'}}>
                    {template.title.en}
                  </td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    {template.targetMin === template.targetMax
                      ? template.targetMin
                      : `${template.targetMin}-${template.targetMax}`}
                  </td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    {template.rewardPoints}
                  </td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    {template.weight}
                  </td>
                  <td style={{...cellStyle, textAlign: 'right'}}>
                    <button
                      type="button"
                      onClick={() => {
                        setNotice(null);
                        setEditing(template);
                      }}
                      style={smallSecondaryButtonStyle}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// ---------- Shared bits ----------

const Pager: React.FC<{
  page: number;
  maxPage: number;
  onChange: (page: number) => void;
  isFetching?: boolean;
}> = ({page, maxPage, onChange, isFetching}) => {
  const hasPrev = page > 1 && !isFetching;
  const hasNext = page < maxPage && !isFetching;
  return (
    <div style={paginationStyle}>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={!hasPrev}
        style={hasPrev ? secondaryButtonStyle : disabledButtonStyle}>
        Prev
      </button>
      <span style={{color: '#999'}}>
        Page {page} of {maxPage}
      </span>
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={!hasNext}
        style={hasNext ? secondaryButtonStyle : disabledButtonStyle}>
        Next
      </button>
    </div>
  );
};

export const ChallengesScreen: React.FC = () => (
  <div style={screenStyle}>
    <div style={{maxWidth: '1100px', margin: '0 auto'}}>
      <h1 style={titleStyle}>Challenges</h1>
      <ChallengesPanel />
      <TemplatesPanel />
    </div>
  </div>
);

// ---------- Styles ----------

const sectionStyle: React.CSSProperties = {
  ...panelStyle,
  marginBottom: '24px',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#fff',
  margin: '0 0 16px',
  fontSize: '1.2rem',
};

const editorTitleStyle: React.CSSProperties = {
  color: '#fff',
  margin: '0 0 12px',
  fontSize: '1.1rem',
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  marginBottom: '16px',
};

const loadingStyle: React.CSSProperties = {
  color: '#999',
  margin: 0,
};

const hintStyle: React.CSSProperties = {
  color: '#777',
  fontSize: '0.75rem',
  margin: '2px 0 0',
};

const groupTitleStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.75rem',
  margin: '8px 0 6px',
};

const localizedGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '12px',
  marginBottom: '14px',
};

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '14px',
  alignItems: 'start',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: '80px',
  padding: '8px 12px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const disabledInputStyle: React.CSSProperties = {
  ...inputStyle,
  color: '#555',
  cursor: 'not-allowed',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#ddd',
  fontSize: '0.85rem',
  height: '40px',
  alignSelf: 'end',
};

const pickerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  marginTop: '8px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid #333',
};

const pickerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '6px 10px',
  borderRadius: '8px',
  backgroundColor: '#2a2a2a',
  fontSize: '0.85rem',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const headStyle: React.CSSProperties = {
  textAlign: 'left',
  color: '#999',
  fontWeight: 'normal',
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: '1px solid #333',
  whiteSpace: 'nowrap',
};

const rowStyle: React.CSSProperties = {
  borderBottom: '1px solid #2e2e2e',
};

const cellStyle: React.CSSProperties = {
  padding: '8px',
  color: '#ddd',
  verticalAlign: 'middle',
};

const smallButton = (base: React.CSSProperties): React.CSSProperties => ({
  ...base,
  padding: '6px 12px',
  fontSize: '0.75rem',
});

const smallPrimaryButtonStyle = smallButton(primaryButtonStyle);
const smallSecondaryButtonStyle = smallButton(secondaryButtonStyle);
const smallDangerButtonStyle = smallButton(dangerButtonStyle);
const smallDisabledButtonStyle = smallButton(disabledButtonStyle);

const paginationStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '16px',
};
